import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Save, Eye, Plus, Trash2, ArrowUp, ArrowDown, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { getCmsLayout, saveCmsLayoutDraft, publishCmsLayout, CmsLayout } from "@/data/cmsStore";
import { Copy, LayoutTemplate, MinusSquare, LayoutGrid, Menu as MenuIcon } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useI18n } from "@/i18n/I18nProvider";

interface NavLinkConfig {
  label_fr: string;
  label_ar: string;
  label_zgh?: string;
  href: string;
  category_fr?: string;
  category_ar?: string;
  category_zgh?: string;
}

export type NavStyle = 'classic' | 'minimalist' | 'megamenu' | 'hamburger';

const defaultLinks: NavLinkConfig[] = [
  { label_fr: "Pitcher", label_ar: "اقتراح", label_zgh: "ⵙⵎⵏⵉⴷ", href: "/pitcher", category_fr: "S'exprimer", category_ar: "عبر", category_zgh: "ⵙⴰⵡⵍ" },
  { label_fr: "Poster", label_ar: "نقاش", label_zgh: "ⴼⵙⵔ", href: "/poster", category_fr: "S'exprimer", category_ar: "عبر", category_zgh: "ⵙⴰⵡⵍ" },
  { label_fr: "Scroller", label_ar: "تصفح", label_zgh: "ⵣⵣⵔⵉ", href: "/scroller", category_fr: "S'informer", category_ar: "تعرف", category_zgh: "ⵉⵙⵉⴳⴳⵍ" },
  { label_fr: "Watcher", label_ar: "مشاهدة", label_zgh: "ⴰⵏⵏⴰⵢ", href: "/watcher", category_fr: "S'informer", category_ar: "تعرف", category_zgh: "ⵉⵙⵉⴳⴳⵍ" },
  { label_fr: "Politiquer", label_ar: "سياسة", label_zgh: "ⵙⴽⵔ ⵜⴰⵙⵔⵜⵉⵜ", href: "/politiquer", category_fr: "S'informer", category_ar: "تعرف", category_zgh: "ⵉⵙⵉⴳⴳⵍ" },
  { label_fr: "Logguer", label_ar: "رقمنة", label_zgh: "ⵙⵎⵓⵟⵟⵓⵏ", href: "/logguer", category_fr: "S'engager", category_ar: "شارك", category_zgh: "ⴷⵔⵓ" },
];

const AdminNavbarPage = () => {
  const { isRtl } = useI18n();
  const { toast } = useToast();
  
  const [layout, setLayout] = useState<CmsLayout>([]);
  const [links, setLinks] = useState<NavLinkConfig[]>([]);
  const [navStyle, setNavStyle] = useState<NavStyle>('classic');
  const [bgImageUrl, setBgImageUrl] = useState<string>('');

  useEffect(() => {
    loadDraft();
  }, []);

  const loadDraft = () => {
    const draft = getCmsLayout("global", true);
    setLayout(draft);
    
    let loadedLinks = [...defaultLinks];
    
    const navSection = draft.find(s => s.type === "NavbarData");
    if (navSection) {
      if (navSection.content?.style) {
        setNavStyle(navSection.content.style as NavStyle);
      }
      if (navSection.content?.bgImageUrl) {
        setBgImageUrl(navSection.content.bgImageUrl);
      }
      if (navSection.content?.links) {
        try {
          const parsed = JSON.parse(navSection.content.links);
          if (parsed && Array.isArray(parsed) && parsed.length > 0) {
            loadedLinks = parsed.map((pLink: any, i: number) => {
              const dLink = defaultLinks.find(d => d.href === pLink.href) || defaultLinks[i] || {} as any;
              return {
                ...pLink,
                label_fr: dLink.label_fr || pLink.label_fr,
                label_ar: dLink.label_ar || pLink.label_ar,
                label_zgh: dLink.label_zgh || pLink.label_zgh,
                category_zgh: pLink.category_zgh || dLink.category_zgh,
              };
            });
          }
        } catch (e) {
          // Keep defaults
        }
      }
    }
    setLinks(loadedLinks);
  };

  const handleSaveDraft = () => {
    const newLayout = layout.map(s => {
      if (s.type === "NavbarData") {
        return { ...s, content: { ...s.content, links: JSON.stringify(links), style: navStyle, bgImageUrl } };
      }
      return s;
    });
    
    setLayout(newLayout);
    saveCmsLayoutDraft("global", newLayout);
    
    toast({
      title: "Navigation sauvegardée",
      description: "Regardez la barre de navigation en haut de l'écran pour voir les changements en direct.",
    });
  };

  const handlePublish = () => {
    handleSaveDraft();
    const draft = getCmsLayout("global", true);
    publishCmsLayout("global", draft);
    
    toast({
      title: "Menu publié",
      description: "La nouvelle structure de navigation est désormais en ligne.",
      variant: "default"
    });
  };

  const addLink = () => {
    setLinks([...links, { label_fr: "Nouveau Lien", label_ar: "رابط جديد", label_zgh: "ⴰⵣⴷⴰⵢ ⴰⵎⴰⵢⵏⵓ", href: "/" }]);
  };

  const updateLink = (idx: number, key: keyof NavLinkConfig, val: string) => {
    const updated = [...links];
    updated[idx][key] = val;
    setLinks(updated);
  };

  const removeLink = (idx: number) => {
    setLinks(links.filter((_, i) => i !== idx));
  };

  const moveLink = (idx: number, direction: -1 | 1) => {
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= links.length) return;
    const newLinks = [...links];
    const temp = newLinks[idx];
    newLinks[idx] = newLinks[newIdx];
    newLinks[newIdx] = temp;
    setLinks(newLinks);
  };

  return (
    <div className="space-y-6" dir={isRtl ? "rtl" : "ltr"}>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b pb-6">
        <div>
          <h1 className="text-3xl font-display font-bold">Gestion du Menu (Navigation)</h1>
          <p className="text-muted-foreground">Personnalisez les liens présents dans l'en-tête du site.</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleSaveDraft} className="gap-2">
            <Save className="w-4 h-4" /> Sauvegarder Brouillon
          </Button>
          <Button onClick={handlePublish} className="gap-2 shadow-[var(--shadow-elevated)]">
            <Eye className="w-4 h-4" /> Publier En Ligne
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-6">
          <Card className="border-primary/20 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex flex-col md:flex-row md:items-center justify-between gap-2">
                <span>Style Visuel de la Navigation</span>
              </CardTitle>
              <CardDescription>
                Testez ces modes en direct. Les menus se mettront à jour immédiatement.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button 
                  onClick={() => setNavStyle('classic')} 
                  className={`p-4 border rounded-xl flex items-start gap-3 transition-all text-left ${navStyle === 'classic' ? 'border-primary ring-1 ring-primary bg-primary/5' : 'hover:bg-muted/50'}`}
                >
                  <LayoutTemplate className={`w-5 h-5 shrink-0 mt-0.5 ${navStyle === 'classic' ? 'text-primary' : 'text-muted-foreground'}`} />
                  <div>
                    <h4 className="font-semibold text-sm">Classique</h4>
                    <p className="text-xs text-muted-foreground mt-1">Tous les mots d'actions visibles et espacés horizontalement.</p>
                  </div>
                </button>
                <button 
                  onClick={() => setNavStyle('minimalist')} 
                  className={`p-4 border rounded-xl flex items-start gap-3 transition-all text-left ${navStyle === 'minimalist' ? 'border-primary ring-1 ring-primary bg-primary/5' : 'hover:bg-muted/50'}`}
                >
                  <MinusSquare className={`w-5 h-5 shrink-0 mt-0.5 ${navStyle === 'minimalist' ? 'text-primary' : 'text-muted-foreground'}`} />
                  <div>
                    <h4 className="font-semibold text-sm">Minimaliste (Compact)</h4>
                    <p className="text-xs text-muted-foreground mt-1">Compresse "Voter" et "Mes Alertes" pour aérer les options.</p>
                  </div>
                </button>
                <button 
                  onClick={() => setNavStyle('megamenu')} 
                  className={`p-4 border rounded-xl flex items-start gap-3 transition-all text-left ${navStyle === 'megamenu' ? 'border-primary ring-1 ring-primary bg-primary/5' : 'hover:bg-muted/50'}`}
                >
                  <LayoutGrid className={`w-5 h-5 shrink-0 mt-0.5 ${navStyle === 'megamenu' ? 'text-primary' : 'text-muted-foreground'}`} />
                  <div>
                    <h4 className="font-semibold text-sm">Méga-Menu (Thématique)</h4>
                    <p className="text-xs text-muted-foreground mt-1">Regroupe vos liens au sein de Dropdowns selon leur "Catégorie".</p>
                  </div>
                </button>
                <button 
                  onClick={() => setNavStyle('hamburger')} 
                  className={`p-4 border rounded-xl flex items-start gap-3 transition-all text-left ${navStyle === 'hamburger' ? 'border-primary ring-1 ring-primary bg-primary/5' : 'hover:bg-muted/50'}`}
                >
                  <MenuIcon className={`w-5 h-5 shrink-0 mt-0.5 ${navStyle === 'hamburger' ? 'text-primary' : 'text-muted-foreground'}`} />
                  <div>
                    <h4 className="font-semibold text-sm">Style Apple (Hamburger)</h4>
                    <p className="text-xs text-muted-foreground mt-1">Très épurée. Les liens sont cachés derrière une simple icône ☰.</p>
                  </div>
                </button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-primary/20 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">Image de Fond (Optionnel)</CardTitle>
              <CardDescription>Ajoutez une image de fond personnalisée pour votre barre de navigation.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <Label>URL de l'image ou Fichier</Label>
                <div className="flex items-center gap-2">
                  <Input 
                    type="file" 
                    accept="image/*" 
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = (event) => setBgImageUrl(event.target?.result as string);
                      reader.readAsDataURL(file);
                    }} 
                    className="w-full md:w-1/2"
                  />
                  <Input 
                    placeholder="Ou collez une URL d'image (https://...)" 
                    value={bgImageUrl.startsWith('data:') ? '' : bgImageUrl} 
                    onChange={e => setBgImageUrl(e.target.value)}
                    className="flex-1"
                  />
                  {bgImageUrl && (
                    <Button variant="ghost" onClick={() => setBgImageUrl('')} className="shrink-0 text-destructive"><Trash2 className="w-4 h-4"/></Button>
                  )}
                </div>
                {bgImageUrl && (
                  <div className="mt-4 rounded-xl border overflow-hidden h-16 w-full relative">
                    <img src={bgImageUrl} alt="Background preview" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div>
                <CardTitle className="text-lg">Liens de Navigation (${links.length})</CardTitle>
                <CardDescription>
                  Gérez l'ordre et l'URL de vos pages.
                </CardDescription>
              </div>
              <Button size="sm" onClick={addLink} className="gap-1">
                <Plus className="w-4 h-4" /> Ajouter
              </Button>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              {links.map((link, idx) => (
                <div key={idx} className="p-4 bg-muted/40 border rounded-xl flex flex-col md:flex-row gap-4 relative group">
                  <div className="flex flex-row md:flex-col gap-1 items-center justify-center border-b md:border-b-0 md:border-r border-border/50 pb-3 md:pb-0 md:pr-4">
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveLink(idx, -1)} disabled={idx === 0}>
                      <ArrowUp className="w-4 h-4" />
                    </Button>
                    <span className="text-xs font-bold w-4 text-center">{idx + 1}</span>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveLink(idx, 1)} disabled={idx === links.length - 1}>
                      <ArrowDown className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs">Libellé (Français)</Label>
                      <Input value={link.label_fr} onChange={e => updateLink(idx, "label_fr", e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Libellé (Arabe)</Label>
                      <Input dir="rtl" value={link.label_ar} onChange={e => updateLink(idx, "label_ar", e.target.value)} />
                    </div>
                    <div className="space-y-1 md:col-span-2">
                      <Label className="text-xs">Libellé (Amazigh)</Label>
                      <Input dir="ltr" value={link.label_zgh || ""} onChange={e => updateLink(idx, "label_zgh", e.target.value)} />
                    </div>
                    {navStyle === 'megamenu' && (
                      <>
                        <div className="space-y-1">
                          <Label className="text-xs text-primary">Catégorie Dropdown (Fr) - Optionnel</Label>
                          <Input value={link.category_fr || ""} onChange={e => updateLink(idx, "category_fr", e.target.value)} placeholder="Ex: S'informer" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-primary">Catégorie Dropdown (Ar) - Optionnel</Label>
                          <Input dir="rtl" value={link.category_ar || ""} onChange={e => updateLink(idx, "category_ar", e.target.value)} placeholder="معلومات" />
                        </div>
                        <div className="space-y-1 md:col-span-2">
                          <Label className="text-xs text-primary">Catégorie Dropdown (Amz) - Optionnel</Label>
                          <Input dir="ltr" value={link.category_zgh || ""} onChange={e => updateLink(idx, "category_zgh", e.target.value)} placeholder="ⵜⴰⴳⴳⴰⵢⵜ" />
                        </div>
                      </>
                    )}
                    <div className={navStyle === 'megamenu' ? "space-y-1 md:col-span-2" : "space-y-1 md:col-span-2"}>
                      <Label className="text-xs flex items-center justify-between">
                        URL de Destination
                        <span className="text-[10px] text-muted-foreground font-normal">Utilisez / pour une page ou # pour faire défiler vers une section de l'accueil</span>
                      </Label>
                      <div className="flex">
                        <Input value={link.href} onChange={e => updateLink(idx, "href", e.target.value)} className="rounded-r-none font-mono text-sm" placeholder="/pourquoi ou #partis" />
                        <Button variant="outline" className="rounded-l-none px-3" asChild>
                          <a href={link.href} target="_blank" rel="noreferrer" title="Tester le lien"><ExternalLink className="w-4 h-4" /></a>
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="absolute -top-3 -right-3 md:top-auto md:right-4 md:relative h-full flex items-center">
                    <Button variant="destructive" size="icon" className="h-8 w-8 rounded-full shadow-sm" onClick={() => removeLink(idx)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
              
              {links.length === 0 && (
                <div className="text-center py-12 border-2 border-dashed rounded-xl text-muted-foreground">
                  <p>Aucun lien configuré.</p>
                  <p className="text-sm">Les paramètres par défaut de l'application seront utilisés.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        <div className="lg:col-span-4">
          <Card className="bg-primary/5 border-primary/20 sticky top-24">
            <CardHeader>
              <CardTitle className="text-primary flex items-center gap-2">
                <Eye className="w-5 h-5" /> Prévisualisation Live
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Pas besoin d'un cadre de simulation compliqué !
              </p>
              <p className="text-sm">
                Dès que vous appuyez sur <strong className="text-foreground">"Sauvegarder Brouillon"</strong>, 
                vous verrez <strong className="text-accent text-lg">la vraie barre de navigation</strong> tout en haut de votre écran se mettre à jour instantanément.
              </p>
              <div className="mt-6 p-4 bg-background rounded-lg border text-xs text-center text-muted-foreground">
                Note : Les boutons "S'inscrire" et "Mon Espace" ainsi que le sélecteur de langue sont des éléments système fixes qui ne peuvent pas être supprimés ici.
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminNavbarPage;
